import { Col, Form, Row, Switch, Card, InputNumber, message } from "antd";
import { useQuote } from "../QuoteProvider";
import { StyledFormItem } from "../StyledQuoteInput";
import QuoteButton from "../components/QuoteButton/QuoteButton";
import { publicImagePath } from "../../../utils/uploadedImage";
import { useIsMobile } from "../MobileBreakpointContext";
import { useTranslationNamespace } from "../../../hooks/useTranslationNamespace";

function computeBmi(
  weight: any,
  heightFt: any,
  heightIn: any,
): number | null {
  const w = Number(weight);
  const ft = Number(heightFt);
  const inches = Number(heightIn);
  if (!w || !ft) return null;
  const totalInches = ft * 12 + (inches || 0);
  if (totalInches <= 0) return null;
  return (w / (totalInches * totalInches)) * 703;
}

type StepMedicalHistoryProps = {
  next: (override?: boolean) => void;
};

const StepMedicalHistory = ({ next }: StepMedicalHistoryProps) => {
  const quoteData = useQuote();
  const isMobile = useIsMobile();
  const { t } = useTranslationNamespace("quote");

  const [form] = Form.useForm();

  const bmi = computeBmi(quoteData.weight, quoteData.heightFt, quoteData.heightIn);

  return (
    <div style={{ textAlign: "left" }}>
      <h1
        style={{
          fontSize: isMobile ? "22px" : "inherit",
          textAlign: "center",
          marginBottom: isMobile ? "8px" : "24px",
        }}
      >
        <span style={{ color: "black", fontWeight: 700 }}>{t("medical.title")}</span>
      </h1>

      <Row>
        <Col
          span={isMobile ? 24 : 11}
          offset={!isMobile ? 1 : 0}
          style={{
            padding: isMobile ? "12px 12px 0px 12px" : "15px",
            paddingBottom: 0,
          }}
        >
          <Card style={{ marginBottom: "0px", padding: "0px" }}>
            <Form
              layout="vertical"
              form={form}
              initialValues={{ layout: "vertical" }}
            >
              <StyledFormItem
                label={t("medical.ageLabel")}
                required
                style={{ marginBottom: "8px" }}
              >
                <InputNumber
                  min={18}
                  max={99}
                  inputMode="numeric"
                  value={quoteData.age ? Number(quoteData.age) : undefined}
                  onChange={(v) =>
                    quoteData.setAge(v == null ? "" : String(v))
                  }
                  style={{ width: "100%" }}
                  placeholder={t("medical.agePlaceholder")}
                />
              </StyledFormItem>

              <StyledFormItem
                label={t("medical.weightLabel")}
                required
                style={{ marginBottom: "8px" }}
              >
                <InputNumber
                  min={50}
                  max={600}
                  inputMode="numeric"
                  value={quoteData.weight ? Number(quoteData.weight) : undefined}
                  onChange={(v) =>
                    quoteData.setWeight(v == null ? "" : String(v))
                  }
                  style={{ width: "100%" }}
                  placeholder={t("medical.weightPlaceholder")}
                />
              </StyledFormItem>

              <StyledFormItem
                label={t("medical.heightLabel")}
                required
                style={{ marginBottom: "8px" }}
              >
                <Row gutter={8}>
                  <Col span={12}>
                    <InputNumber
                      min={3}
                      max={8}
                      inputMode="numeric"
                      value={
                        quoteData.heightFt
                          ? Number(quoteData.heightFt)
                          : undefined
                      }
                      onChange={(v) =>
                        quoteData.setHeightFt(v == null ? "" : String(v))
                      }
                      style={{ width: "100%" }}
                      placeholder={t("medical.heightFt")}
                      addonAfter="ft"
                    />
                  </Col>
                  <Col span={12}>
                    <InputNumber
                      min={0}
                      max={11}
                      inputMode="numeric"
                      value={
                        quoteData.heightIn !== "" &&
                        quoteData.heightIn != null
                          ? Number(quoteData.heightIn)
                          : undefined
                      }
                      onChange={(v) =>
                        quoteData.setHeightIn(v == null ? "" : String(v))
                      }
                      style={{ width: "100%" }}
                      placeholder={t("medical.heightIn")}
                      addonAfter="in"
                    />
                  </Col>
                </Row>
              </StyledFormItem>

              {bmi != null && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    marginBottom: "12px",
                    paddingLeft: "2px",
                  }}
                >
                  {t("medical.bmiCaption", { value: bmi.toFixed(1) })}
                </div>
              )}

              <StyledFormItem style={{ marginBottom: "4px" }}>
                <Row align="middle" wrap={false}>
                  <Col
                    flex="auto"
                    style={{ paddingRight: "16px", color: "#212427" }}
                  >
                    {t("medical.smoker")}
                  </Col>
                  <Col flex="none">
                    <Switch
                      defaultChecked={quoteData.smoker}
                      onChange={(value) => {
                        quoteData.setSmoker(value);
                      }}
                    />
                  </Col>
                </Row>
              </StyledFormItem>

              <StyledFormItem style={{ marginBottom: "4px" }}>
                <Row align="middle" wrap={false}>
                  <Col
                    flex="auto"
                    style={{ paddingRight: "16px", color: "#212427" }}
                  >
                    {t("medical.medicalConditions")}
                  </Col>
                  <Col flex="none">
                    <Switch
                      defaultChecked={quoteData.hasMedicalConditions}
                      onChange={(value) => {
                        quoteData.setHasMedicalConditions(value);
                      }}
                    />
                  </Col>
                </Row>
              </StyledFormItem>

              <StyledFormItem style={{ marginBottom: "4px" }}>
                <Row align="middle" wrap={false}>
                  <Col
                    flex="auto"
                    style={{ paddingRight: "16px", color: "#212427" }}
                  >
                    {t("medical.priorSurgery")}
                  </Col>
                  <Col flex="none">
                    <Switch
                      defaultChecked={quoteData.priorCosmeticSurgery}
                      onChange={(value) => {
                        quoteData.setPriorCosmeticSurgery(value);
                      }}
                    />
                  </Col>
                </Row>
              </StyledFormItem>

              <StyledFormItem style={{ marginBottom: "12px" }}>
                <Row align="middle" wrap={false}>
                  <Col
                    flex="auto"
                    style={{ paddingRight: "16px", color: "#212427" }}
                  >
                    {t("medical.weightLoss")}
                  </Col>
                  <Col flex="none">
                    <Switch
                      defaultChecked={quoteData.lost50pounds}
                      onChange={(value) => {
                        quoteData.setLost50Pounds(value);
                      }}
                    />
                  </Col>
                </Row>
              </StyledFormItem>
            </Form>
          </Card>

          <div style={{ marginTop: "0px" }}>
            <div className="quote-button-mobile-container">
              <QuoteButton
                onClick={() => {
                  const a = Number(quoteData.age);
                  const w = Number(quoteData.weight);
                  const ft = Number(quoteData.heightFt);
                  if (!a) {
                    message.error(t("medical.ageRequired"));
                    return;
                  }
                  if (!w || !ft) {
                    message.error(t("medical.heightWeightRequired"));
                    return;
                  }
                  next();
                }}
              >
                {t("medical.getInstantQuote")}
              </QuoteButton>
            </div>
          </div>

          {isMobile && (
            <div
              style={{
                textAlign: "center",
                marginTop: "24px",
                paddingBottom: "80px",
              }}
            >
              <img
                src={publicImagePath(
                  "1680827748-breast-bg-real-patient-ed.jpg",
                )}
                alt="Real Patient Example"
                style={{
                  width: "100%",
                  borderRadius: "8px",
                  display: "block",
                  marginBottom: "16px",
                }}
              />
              <Card
                style={{
                  backgroundColor: "#f0f4f8",
                  border: "1px solid #d0dce6",
                  textAlign: "center",
                  padding: "3px 8px",
                }}
                bodyStyle={{ padding: "3px 8px" }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#065f46",
                    backgroundColor: "#d1fae5",
                    padding: "6px 12px",
                    borderRadius: "999px",
                    display: "inline-block",
                    marginBottom: "4px",
                  }}
                >
                  ✓ {t("medical.realPatient")}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#4b5563",
                    lineHeight: "1.6",
                  }}
                >
                  {t("medical.realPatientDescription")}
                </div>
              </Card>
            </div>
          )}
        </Col>
        <Col span={isMobile ? 24 : 11} style={{ padding: isMobile ? "12px" : "15px" }}>
          {!isMobile && (
            <div style={{ textAlign: "center" }}>
              <img
                src={publicImagePath(
                  "1680827748-breast-bg-real-patient-ed.jpg",
                )}
                alt="Real Patient Example"
                style={{
                  width: "100%",
                  borderRadius: "8px",
                  display: "block",
                  marginBottom: "16px",
                }}
              />
              <Card
                style={{
                  backgroundColor: "#f0f4f8",
                  border: "1px solid #d0dce6",
                  textAlign: "center",
                  padding: "3px 8px",
                }}
                bodyStyle={{ padding: "3px 8px" }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#065f46",
                    backgroundColor: "#d1fae5",
                    padding: "6px 12px",
                    borderRadius: "999px",
                    display: "inline-block",
                    marginBottom: "4px",
                  }}
                >
                  ✓ {t("medical.realPatient")}
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#4b5563",
                    lineHeight: "1.6",
                  }}
                >
                  {t("medical.realPatientDescription")}
                </div>
              </Card>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default StepMedicalHistory;
